'use client';

import React, { useEffect, useRef, useState } from 'react';
import { cn } from '../lib/utils';
import { useCompositionDensity } from './composition-density';

/** Viewport-docked actions retain their container width, reserve space and clear the keyboard. */
export function StickyActions({
  label,
  role,
  children,
  className,
}: {
  label: string;
  role?: React.AriaRole;
  children: React.ReactNode;
  className?: string;
}) {
  const clearanceRef = useRef<HTMLDivElement>(null);
  const [dock, setDock] = useState<{ left: number; width: number } | null>(null);
  const barRef = useRef<HTMLElement>(null);
  const [barHeight, setBarHeight] = useState(0);
  const [keyboardInset, setKeyboardInset] = useState(0);
  const density = useCompositionDensity();
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const update = () =>
      setKeyboardInset(
        viewport.scale === 1
          ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)
          : 0,
      );
    update();
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);
  useEffect(() => {
    const bar = barRef.current;
    if (!bar) return;
    const measure = () => {
      setBarHeight(bar.getBoundingClientRect().height);
      const bounds = clearanceRef.current?.getBoundingClientRect();
      if (bounds && bounds.width > 0) setDock({ left: bounds.left, width: bounds.width });
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(bar);
    if (clearanceRef.current) observer?.observe(clearanceRef.current);
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, []);
  const reasons = new Set<string>();
  const collect = (nodes: React.ReactNode) =>
    React.Children.forEach(nodes, (node) => {
      if (
        !React.isValidElement<{
          disabled?: boolean;
          disabledReason?: string;
          'aria-describedby'?: string;
          children?: React.ReactNode;
          primary?: React.ReactNode;
          secondary?: React.ReactNode;
        }>(node)
      )
        return;
      if (node.props.disabled && node.props.disabledReason && !node.props['aria-describedby'])
        reasons.add(node.props.disabledReason);
      collect(node.props.children);
      collect(node.props.primary);
      collect(node.props.secondary);
    });
  collect(children);
  return (
    <>
      <div
        ref={clearanceRef}
        aria-hidden="true"
        data-action-bar-clearance
        className="shrink-0"
        style={{ height: barHeight }}
      />
      <footer
        ref={barRef}
        aria-label={label}
        role={role}
        className={cn(
          'sticky bottom-0 z-20 flex min-w-0 flex-wrap items-center justify-end gap-2 rounded-card border border-border bg-surface-sheet px-4 py-2 shadow-popover',
          density === 'comfort' && '[&_button]:min-h-12 [&_a]:min-h-12',
          className,
        )}
        style={{
          ...(dock ? ({ position: 'fixed', left: dock.left, width: dock.width } as const) : {}),
          bottom: keyboardInset,
          paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom))',
        }}
      >
        <div className="w-full min-w-0 space-y-2">
          <div className="flex min-w-0 flex-wrap items-baseline justify-end gap-2">
            {reasons.size > 0 && (
              <div className="mr-auto min-w-0 flex-1 break-words text-caption text-text-muted [overflow-wrap:anywhere]">
                {Array.from(reasons).map((reason) => (
                  <p key={reason}>{reason}</p>
                ))}
              </div>
            )}
            {children}
          </div>
        </div>
      </footer>
    </>
  );
}
